import { type AiRunStatus, type AiRunStep, type ClarifyQuestion } from '@apm/contracts';

/**
 * Lượt chạy AI đang được theo dõi — APM-1302.
 *
 * `apply` do **màn hình gọi** đặt, không phải do store nghĩ ra: "áp dụng" nghĩa
 * gì thì chỉ chỗ mở lượt chạy biết (thuyết minh thì hiện phụ đề, quét rủi ro
 * thì mở bảng gợi ý, sinh kế hoạch thì sang bước duyệt nháp). Đặt logic ấy vào
 * store là buộc store biết mọi tính năng AI của sản phẩm.
 */
export interface AiRunView {
  readonly runId: string;
  readonly skill: string;
  /** Tiêu đề tiếng Việt hiện trên bảng. */
  readonly label: string;
  readonly status: AiRunStatus;
  readonly steps: readonly AiRunStep[];
  readonly apply: { readonly label: string; readonly run: () => void | Promise<void> } | null;
  /**
   * Một dòng nói **kết quả là gì**, do màn hình gọi đặt từ dữ liệu đã nhận.
   *
   * Bước `done` của server chỉ nói "xong, tốn bao nhiêu" — nó không được đọc
   * nội dung gợi ý để tóm tắt, vì gợi ý đi về theo phản hồi HTTP (đường đã qua
   * phân quyền) chứ không qua stream. Nên câu tóm tắt phải do phía nhận đặt.
   */
  readonly result: string | null;
  readonly applied: boolean;
  /** Lỗi của lời gọi HTTP — khác với bước `error` do server kể lại. */
  readonly error: string | null;
  /**
   * Trợ lý đang hỏi lại và **đây là chỗ trả lời** — APM-UX-ai1.
   *
   * Trước đây bước `question` chỉ được *kể lại* trên bảng: người dùng đọc thấy
   * "AI hỏi lại" kèm ba câu hỏi, rồi không có ô nào để gõ. Ô trả lời sống ở
   * wizard kế hoạch, mà wizard có thể đang đóng, đang ở tab khác, hoặc bị chính
   * bảng này che. Một câu hỏi không trả lời được là một lượt chạy đã trả tiền
   * rồi bỏ.
   *
   * `submit` do **màn hình gọi** đặt, cùng lý lẽ với `apply`: chỉ chỗ mở lượt
   * chạy biết trả lời thì gửi đi đâu.
   */
  readonly clarify: AiClarifyPrompt | null;
  /**
   * Văn xuôi model **đang gõ** — 2026-09-29. Bản chụp mới nhất từ sự kiện SSE
   * `delta`; không phải bước, nên không có `seq` và không nằm trong `steps`.
   * `null` khi không có gì đang gõ; bước thật kế tiếp xoá nó.
   */
  readonly live: string | null;
  /**
   * Câu trả lời **đang viết** cho khung chat — APM-CHAT2-m, sự kiện SSE `answer`. Khác `live`:
   * bước thật không xoá nó, vì câu trả lời cuối tới qua phản hồi HTTP của chính lượt chat.
   */
  readonly answer: string | null;
}

/** Câu hỏi của trợ lý cùng đường gửi câu trả lời về. */
export interface AiClarifyPrompt {
  readonly questions: readonly ClarifyQuestion[];
  readonly submit: (answers: Record<string, string>) => void | Promise<void>;
  /** Dự án của lượt chạy — để mỗi câu trả lời kèm nút "Ghi nhớ điều này?" (APM-H-j). */
  readonly projectId?: string;
}

interface Snapshot {
  readonly run: AiRunView | null;
  /**
   * Bảng bên phải có đang mở không — **độc lập** với việc có lượt chạy hay không.
   *
   * Tách ra vì từ APM-UX-ai1 bảng còn là chỗ xem lại **những lượt đã chạy**:
   * người dùng mở nó từ thanh trên cùng khi chẳng có gì đang chạy, để tìm lại
   * kết quả hôm qua. Gắn hiển thị vào `run !== null` như trước thì nút mở nhanh
   * ấy không mở được gì.
   */
  readonly open: boolean;
}

/**
 * Chờ bao lâu trước khi cắt stream sau khi lời gọi HTTP đã xong.
 *
 * ⚠️ Không cắt **ngay**: server đẩy bước `done` vào stream rồi mới trả body
 * HTTP, và không có gì đảm bảo hai đường đó tới client theo thứ tự ấy. Cắt
 * ngay thì đúng những bước cuối — `Suy nghĩ` và `done`, tức phần kết luận —
 * có thể không bao giờ hiện ra.
 */
const SETTLE_GRACE_MS = 3_000;

/**
 * Store nhỏ ngoài React, đọc bằng `useSyncExternalStore`.
 *
 * ## Vì sao không phải React Context
 *
 * Lượt chạy được **mở** từ sâu trong cây (một nút trong panel RAID) nhưng được
 * **hiển thị** ở `AppShell`, ngoài mọi route. Context thì provider phải bọc cả
 * hai, và mỗi bước mới re-render toàn bộ cây bên dưới — mà bước tới liên tục
 * trong lúc agent chạy. Ở đây chỉ hai chỗ đăng ký: bảng theo dõi và nút mở.
 *
 * ## Vì sao chỉ giữ MỘT lượt
 *
 * Bảng bên phải trả lời câu "AI đang làm gì" — số ít. Giữ nhiều lượt cùng lúc
 * thì phải có danh sách tab, và không ai bấm hai tính năng AI cùng lúc đủ
 * thường xuyên để trả cái giá đó. Mở lượt mới thì lượt cũ bị thay, và stream
 * của nó bị huỷ ngay tại đây.
 */
class AiRunStore {
  private snapshot: Snapshot = { run: null, open: false };
  private readonly listeners = new Set<() => void>();
  /** Huỷ stream SSE của lượt đang giữ. Do `useAiRun` cắm vào. */
  private abort: (() => void) | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Snapshot => this.snapshot;

  start(input: {
    runId: string;
    skill: string;
    label: string;
    abort: () => void;
    /** `false` = record the run but leave the panel as it is (`TrackOptions.openPanel`). */
    openPanel?: boolean;
  }): void {
    // Lượt trước còn đang nghe thì cắt: một stream mồ côi vẫn tự kết nối lại
    // theo backoff và vẫn đẩy bước vào một bảng không còn ai xem.
    this.abort?.();
    this.abort = input.abort;

    this.set({
      open: input.openPanel === false ? this.snapshot.open : true,
      run: {
        runId: input.runId,
        skill: input.skill,
        label: input.label,
        status: 'running',
        steps: [],
        apply: null,
        result: null,
        applied: false,
        error: null,
        clarify: null,
        live: null,
        answer: null,
      },
    });
  }

  /** Bản chụp text đang gõ. Chỉ nhận khi còn đợi model — lượt đã kết thì bỏ. */
  delta(text: string): void {
    const run = this.snapshot.run;
    if (!run || run.status !== 'running') return;
    if (run.live === text) return;
    this.set({ ...this.snapshot, run: { ...run, live: text } });
  }

  /** Bản chụp câu trả lời đang viết. Lượt đã kết thì bỏ — bản cuối đã có ở phản hồi HTTP. */
  answer(text: string): void {
    const run = this.snapshot.run;
    if (!run || run.status !== 'running') return;
    if (run.answer === text) return;
    this.set({ ...this.snapshot, run: { ...run, answer: text } });
  }

  /** Mở bảng mà không bắt đầu lượt chạy nào — nút trên thanh điều hướng. */
  openPanel(): void {
    if (this.snapshot.open) return;
    this.set({ ...this.snapshot, open: true });
  }

  /**
   * Cất bảng đi mà **không** bỏ lượt đang xem — APM-TOUR-ai.
   *
   * Khác `close()`: `close()` huỷ lượt đang chạy và xoá nó khỏi bảng. Nút "AI
   * tour" chỉ cần dọn chỗ để tour chiếu sáng, nên một lượt đang chạy dở phải
   * còn nguyên khi người dùng mở bảng lại.
   */
  hidePanel(): void {
    if (!this.snapshot.open) return;
    this.set({ ...this.snapshot, open: false });
  }

  togglePanel(): void {
    this.set({ ...this.snapshot, open: !this.snapshot.open });
  }

  /**
   * Gắn câu hỏi và đường gửi câu trả lời — APM-UX-ai1.
   *
   * Gọi sau khi lời gọi HTTP trả về: bước `question` trên stream nói *có* câu
   * hỏi, nhưng chỉ phía nhận kết quả mới biết gửi câu trả lời đi đâu.
   */
  setClarify(runId: string, clarify: AiClarifyPrompt | null): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    // Chốt luôn trạng thái: bước `question` có thể **không** tới được (proxy
    // chặn SSE, kết nối rớt), và khi đó `settle` sẽ chốt "Đã xong" ngay bên
    // dưới một biểu mẫu đang chờ trả lời — hai thông tin trái nhau trên cùng
    // một bảng, và người dùng tin cái nói rằng chẳng còn gì để làm.
    const status: AiRunStatus = clarify && run.status === 'running' ? 'clarifying' : run.status;
    this.set({ ...this.snapshot, run: { ...run, clarify, status } });
  }

  /**
   * Thêm một bước. **Bỏ qua bước đã có** theo `seq`.
   *
   * Chống trùng là bắt buộc, không phải phòng xa: server đóng stream ở bước
   * kết, `connectStream` thấy stream đóng thì tự kết nối lại, và tracker phát
   * bù toàn bộ bước cho người vừa đăng ký. Không lọc theo `seq` thì mỗi lần
   * kết nối lại là cả dấu vết hiện thêm một lần nữa.
   */
  step(step: AiRunStep): void {
    const run = this.snapshot.run;
    if (!run) return;
    if (run.steps.some((existing) => existing.seq === step.seq)) return;

    const status: AiRunStatus =
      step.kind === 'done'
        ? 'done'
        : step.kind === 'error'
          ? 'error'
          : step.kind === 'question'
            ? 'clarifying'
            : run.status;

    // Bước thật thay chỗ bản đang gõ: `prose` của cùng lượt gọi mang đủ chữ.
    this.set({
      ...this.snapshot,
      run: { ...run, steps: [...run.steps, step], status, live: null },
    });

    // Hết việc thì thôi nghe. Server đã đóng phía nó; giữ thêm ở phía này chỉ
    // sinh ra một vòng kết nối lại vô ích.
    if (step.kind === 'done' || step.kind === 'error') {
      this.abort?.();
      this.abort = null;
    }
  }

  /**
   * Nạp dấu vết **bền** sau sự kiện `gap` — APM-H-m: RAM server đã mất run này
   * (restart, hết TTL) nhưng DB còn. Bước đã có (theo `seq`) bỏ qua; trạng thái
   * lấy theo bản bền. Run còn `running` thì giữ stream: `connectStream` sẽ thử
   * lại và nhận `gap` mới khi job dọn run treo ghi bước kết.
   */
  hydrate(runId: string, steps: readonly AiRunStep[], status: AiRunStatus): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    const known = new Set(run.steps.map((step) => step.seq));
    const merged = [...run.steps, ...steps.filter((step) => !known.has(step.seq))].sort(
      (a, b) => a.seq - b.seq,
    );
    /**
     * Trạng thái bền chỉ **ghi đè** khi nó đã kết thúc.
     *
     * Bản bền có thể tụt lại một nhịp: trả lời một nháp cũ dùng lại chính mã
     * dấu vết đã hỏi, nên hàng `ai_run` còn mang `clarifying` của lượt trước
     * trong lúc lượt mới đang chạy. Lấy nguyên trạng thái ấy là nói với người
     * dùng rằng hệ thống vẫn đang chờ họ — ngay sau khi họ vừa trả lời.
     */
    const next: AiRunStatus = status === 'done' || status === 'error' ? status : run.status;
    this.set({ ...this.snapshot, run: { ...run, steps: merged, status: next } });
    if (next === 'done' || next === 'error') {
      this.abort?.();
      this.abort = null;
    }
  }

  /** Gắn hành động "đưa kết quả vào giao diện chính" sau khi lượt chạy xong. */
  setApply(runId: string, apply: AiRunView['apply']): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    this.set({ ...this.snapshot, run: { ...run, apply } });
  }

  setResult(runId: string, result: string): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    this.set({ ...this.snapshot, run: { ...run, result } });
  }

  /**
   * Lời gọi HTTP đã xong tốt đẹp. Nếu chưa có bước kết nào thì chốt là xong.
   *
   * ⚠️ Đây là lưới an toàn cho trường hợp **stream không tới được**: proxy chặn
   * `text/event-stream`, trình duyệt cũ, hoặc kết nối rớt đúng lúc. Không có
   * nó thì bảng hiện kết quả ở chân mà đầu vẫn quay vòng "đang chạy" — hai
   * thông tin trái nhau trên cùng một bảng, và người dùng tin cái tệ hơn.
   */
  settle(runId: string): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    // Đã có kết cục thì thôi.
    if (run.status === 'done' || run.status === 'error') return;
    /**
     * ⚠️ Chặn theo **câu hỏi đang chờ**, không theo trạng thái `clarifying`.
     *
     * Hai cảnh khác hẳn nhau cùng mang trạng thái ấy. Một: lời gọi vừa trả về
     * một câu hỏi, biểu mẫu đang mở — chốt "Đã xong" ngay trên đầu một biểu mẫu
     * chờ người trả lời là nói sai. Hai: đây là lượt **trả lời**, nó dùng lại
     * dấu vết của lượt đã hỏi nên `question` cũ được phát lại và kéo trạng thái
     * sang `clarifying`, trong khi kế hoạch đã về — không chốt thì bảng đứng
     * mãi ở "Đang chờ bạn trả lời", trông y như bấm nút mà chẳng có gì xảy ra.
     *
     * Thứ phân biệt hai cảnh là `clarify`: chỉ cảnh một mới có chỗ để trả lời.
     */
    if (run.clarify) return;

    const abort = this.abort;
    this.abort = null;
    setTimeout(() => abort?.(), SETTLE_GRACE_MS);

    this.set({ ...this.snapshot, run: { ...run, status: 'done', live: null } });
  }

  markApplied(runId: string): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;
    this.set({ ...this.snapshot, run: { ...run, applied: true } });
  }

  /**
   * Lời gọi HTTP thất bại.
   *
   * Giữ `status: 'error'` kể cả khi server chưa kịp kể bước nào — mạng đứt
   * giữa đường là một kết cục, và bảng theo dõi không được quay mãi ở "đang
   * chạy" chỉ vì không ai kể cho nó.
   */
  fail(runId: string, message: string): void {
    const run = this.snapshot.run;
    if (!run || run.runId !== runId) return;

    // Cùng thời gian chờ như `settle`, cùng lý do: server có thể vừa đẩy bước
    // `error` (nói rõ hơn thông điệp HTTP) ngay trước khi trả lỗi.
    const abort = this.abort;
    this.abort = null;
    setTimeout(() => abort?.(), SETTLE_GRACE_MS);

    this.set({ ...this.snapshot, run: { ...run, status: 'error', error: message, live: null } });
  }

  close(): void {
    this.abort?.();
    this.abort = null;
    this.set({ run: null, open: false });
  }

  private set(snapshot: Snapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }
}

export const aiRunStore = new AiRunStore();
